import React from "react";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { CaretDown, CaretUp, ArrowSquareOut } from "@phosphor-icons/react";
import { useNavigation } from "@react-navigation/native";

import type { RootStackParamList } from "../../../navigation/AppNavigator.web";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { Degree } from "../../../graphql/generated/graphql";

interface DegreeRowProps {
  degree: Degree;
  isMenuOpen: boolean;
  onOpenMenu: (e: React.SyntheticEvent, degree: Degree) => void;
}

const DegreeRow: React.FC<DegreeRowProps> = ({ degree, isMenuOpen, onOpenMenu }) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const handleDegreeClick = () => {
    navigation.navigate("Degree Information", {
      degreeId: degree.id,
      degreeName: degree.name,
      returnTo: "Degrees & Programs",
    });
  };

  return (
    <View style={styles.tableRow}>
      <View style={[styles.cell, styles.nameCell]}>
        <View style={styles.degreeNameContent}>
          <TouchableOpacity style={styles.degreeNameContainer} onPress={handleDegreeClick}>
            <CustomText
              text={degree.name}
              weight="medium"
              style={styles.degreeName}
              useTranslationText={false}
              numberOfLines={2}
              ellipsizeMode="tail"
            />
            <ArrowSquareOut size={16} color="#3E68FE" />
          </TouchableOpacity>
        </View>
      </View>

      <View style={[styles.cell, styles.coursesCell]}>
        <View style={styles.coursesContainer}>
          {degree.coursesHasCourse && degree.coursesHasCourse.length > 0 ? (
            degree.coursesHasCourse.slice(0, 3).map((course, index) => (
              <View key={course.id || index} style={styles.coursePill}>
                <CustomText
                  text={course.name || "Unnamed Course"}
                  size="xs"
                  useTranslationText={false}
                  style={styles.coursePillText}
                />
              </View>
            ))
          ) : (
            <CustomText text="No courses assigned" size="xs" style={styles.noCoursesText} />
          )}
          {degree.coursesHasCourse && degree.coursesHasCourse.length > 3 && (
            <View style={styles.coursePill}>
              <CustomText
                text={`+${degree.coursesHasCourse.length - 3} more`}
                size="xs"
                useTranslationText={false}
                style={styles.coursePillText}
              />
            </View>
          )}
        </View>
      </View>

      <View style={[styles.cell, styles.enrollmentCell]}>
        <CustomText
          text={(degree.usersEnrolledIn?.length || 0).toString()}
          weight="bold"
          style={styles.enrollmentText}
          useTranslationText={false}
        />
      </View>

      <View style={[styles.cell, styles.actionsCell]}>
        <CustomButton
          text="Manage"
          size="s"
          rightIcon={isMenuOpen ? <CaretUp size={14} color="#FFFFFF" /> : <CaretDown size={14} color="#475569" />}
          onPress={(e) => onOpenMenu(e, degree)}
          styleType="transparent"
          style={[styles.manageButton, isMenuOpen && styles.manageButtonActive]}
          textStyle={[styles.manageText, isMenuOpen && styles.manageTextActive]}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  nameCell: {
    flex: 3,
  },
  coursesCell: {
    flex: 3,
    alignItems: "flex-start",
  },
  enrollmentCell: {
    flex: 1.25,
  },
  actionsCell: {
    flex: 1.25,
    alignItems: "flex-start",
  },
  degreeNameContent: {
    gap: 4,
  },
  degreeNameContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  degreeName: {
    color: "#3E68FE",
    fontSize: 15,
    cursor: "pointer",
  },
  coursesContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  coursePill: {
    backgroundColor: "#e0f2fe",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: "#bae6fd",
    borderRadius: 16,
  },
  coursePillText: {
    color: "#0369a1",
  },
  noCoursesText: {
    color: "#6B7280",
    fontStyle: "italic",
  },
  enrollmentText: {
    color: "#1E293B",
  },
  manageButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderColor: "#EAECF0",
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderRadius: 6,
  },
  manageText: {
    color: "#475569",
    fontSize: 13,
  },
  manageTextActive: {
    color: "#FFFFFF",
    fontSize: 13,
  },
  manageButtonActive: {
    backgroundColor: "#3E68FE",
  },
});

export default DegreeRow;
